import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, CrmToaster, crmToast, SearchInput, SegTabs } from './crmUi'
import { Button } from '../shared/Button'
import { SkeletonRows } from '../shared/Skeleton'
import {
  getObConfig, getOnboardings, getUsers, moveDealStage, obArchive, obSendOverNotice, obSetOverride, obSetRisk,
  type ObConfig, type Onboarding,
} from './crmApi'
import { attachLiveClock, tzAbbr } from './localTime'
import { ObEmbedModal } from './ObEmbedModal'
import { countryToFlag } from '../../utils/countryFlag'
import { Card, BtnOutline, OB, personColor, useObFont, ClockIcon, WarnIcon } from './obDesign'

/**
 * ONBOARDINGS BOARD (Lee, 28 Sep 2026 — Phase 2 of the Onboardings feature,
 * from the approved sandbox). Three columns (Not Started / In Progress /
 * Complete) computed SERVER-side; dragging a card is a manual override that
 * demands a reason (kept in the audit — the Win Probability pattern) and is
 * cleared by the next automatic transition. Cards reuse the lifecycle-card
 * look: logo, LIVE/Trial badge, local-time chip, plan/hours/stage/meeting
 * rows and the five-segment stage strip. Population comes from the
 * staging.wequote pull (Phase 1).
 */

const RISK_COLOR: Record<string, string> = { Watch: '#b45309', 'At Risk': '#c2410c', Urgent: '#b91c1c', Reviewed: '#047857' }

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—')

/** Live HH:MM chip from the staging timezone (same engine as the board). */
const ObTime: React.FC<{ tz?: string | null }> = React.memo(({ tz }) => {
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => (tz ? attachLiveClock(ref.current, tz) : undefined), [tz])
  if (!tz) return null
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-surface-secondary border-border text-txt-tertiary tabular-nums" title={tz}>
      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span ref={ref} /> {tzAbbr(tz)}
    </span>
  )
})

const HoursBar: React.FC<{ used: number; allowance: number | null }> = ({ used, allowance }) => {
  if (!allowance) return <span className="text-[11px] text-txt-tertiary">—</span>
  const over = used > allowance ? Math.round((used - allowance) * 10) / 10 : 0
  const pct = Math.min(100, (used / allowance) * 100)
  const color = over ? '#dc2626' : pct >= 80 ? '#f59e0b' : '#2563eb'
  return (
    <span className="inline-flex items-center gap-1.5">
      {/* "4/4 complete + X over" (Lee, 5 Oct 2026) */}
      <span className={`text-[11px] font-semibold tabular-nums ${over ? 'text-red-600' : 'text-txt-primary'}`}>
        {over ? `${allowance}/${allowance}h + ${over}h over` : `${used} / ${allowance}h`}
      </span>
      <span className="w-16 h-1.5 rounded-full bg-surface-tertiary overflow-hidden inline-block">
        <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </span>
    </span>
  )
}

/** Stage strip (v2 spec): ONE segment per stage that APPLIES — the server's
 *  `segments` already skip hidden Bespoke, unpurchased add-ons and Grow.
 *  Green done, blue partial fill, grey untouched; min-width guard so labels
 *  never overflow the card. */
const StageSegs: React.FC<{ o: Onboarding; cfg: ObConfig }> = ({ o }) => {
  const segs = o.segments && o.segments.length ? o.segments : o.stages.filter((s) => !s.hidden && !s.addon && !s.proposed).map((s) => ({ short: s.short || '', done: s.done, total: s.total, locked: !!s.locked }))
  return (
    <div className="flex gap-1 mt-2.5 min-w-0">
      {segs.map((s, i) => {
        const complete = s.total > 0 && s.done >= s.total
        const partial = !complete && s.done > 0
        return (
          <div key={i} className="flex-1 min-w-0" title={`${s.short}: ${s.done}/${s.total}${s.locked ? ' (locked)' : ''}`}>
            <div className="h-1.5 rounded-full overflow-hidden bg-surface-tertiary">
              <div className="h-full rounded-full" style={{ width: complete ? '100%' : partial ? `${(s.done / Math.max(1, s.total)) * 100}%` : 0, background: complete ? '#10b981' : '#2563eb', opacity: s.locked ? 0.35 : 1 }} />
            </div>
            <p className="text-[8px] mt-0.5 text-center truncate text-txt-tertiary leading-tight">{s.short}</p>
          </div>
        )
      })}
    </div>
  )
}

/** AT RISK table (Phase 5): sortable, inline status + notes for rows with a
 *  risk set; suggested-only rows carry a SUGGESTED chip and open the panel
 *  via the lead's Onboarding tab. */
const RiskTable: React.FC<{
  rows: Onboarding[]
  users: any[]
  agentName: (id?: string | null) => string
  onOpen: (o: Onboarding) => void
  onChanged: () => void
}> = ({ rows, users, agentName, onOpen, onChanged }) => {
  const [sortKey, setSortKey] = useState<'company' | 'owner' | 'login' | 'quote' | 'accepted' | 'status'>('status')
  const [asc, setAsc] = useState(true)
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({})
  const RISK_ORDER = ['Urgent', 'At Risk', 'Watch', 'Reviewed']
  const val = (o: Onboarding) => {
    switch (sortKey) {
      case 'company': return o.companyName.toLowerCase()
      case 'owner': return agentName(o.risk?.ownerId || o.ownerId).toLowerCase()
      case 'login': return o.lastLoginAt || ''
      case 'quote': return o.lastQuoteAt || ''
      case 'accepted': return o.lastQuoteAcceptedAt || ''
      case 'status': return String(o.risk ? RISK_ORDER.indexOf(o.risk.status) : 99)
    }
  }
  const sorted = [...rows].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * (asc ? 1 : -1))
  const TH: React.FC<{ k: typeof sortKey; children: React.ReactNode }> = ({ k, children }) => (
    <th
      onClick={() => (sortKey === k ? setAsc(!asc) : (setSortKey(k), setAsc(true)))}
      className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-txt-tertiary cursor-pointer hover:text-txt-primary select-none whitespace-nowrap"
    >
      {children}{sortKey === k ? (asc ? ' ↑' : ' ↓') : ''}
    </th>
  )
  const d = (s?: string | null) => (s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—')
  if (!rows.length) return <div className="rounded-xl border border-dashed border-border bg-surface p-8 text-center text-[13px] text-txt-secondary max-w-md mx-auto mt-6">Nobody is at risk — no statuses set and no triggers firing.</div>
  return (
    <div className="rounded-xl border border-border bg-surface overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead className="border-b border-border bg-surface-secondary/60">
          <tr>
            <TH k="company">Customer</TH><TH k="owner">Owner</TH><TH k="login">Last Login</TH>
            <TH k="quote">Last Quote</TH><TH k="accepted">Last Accepted</TH>
            <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Risk Reason</th>
            <TH k="status">Status</TH>
            <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sorted.map((o) => (
            <tr key={o.id} className="hover:bg-surface-hover">
              <td className="px-3 py-2 font-bold text-txt-primary cursor-pointer whitespace-nowrap" onClick={() => onOpen(o)}>{o.companyName}</td>
              <td className="px-3 py-2 text-txt-secondary whitespace-nowrap">{agentName(o.risk?.ownerId || o.ownerId)}</td>
              <td className="px-3 py-2 text-txt-secondary tabular-nums">{d(o.lastLoginAt)}</td>
              <td className="px-3 py-2 text-txt-secondary tabular-nums">{d(o.lastQuoteAt)}</td>
              <td className="px-3 py-2 text-txt-secondary tabular-nums">{d(o.lastQuoteAcceptedAt)}</td>
              <td className="px-3 py-2 text-txt-secondary max-w-[220px] truncate" title={(o.risk?.reasons || o.suggestions || []).join(', ')}>
                {(o.risk?.reasons?.length ? o.risk.reasons : o.suggestions).join(' · ') || '—'}{o.risk?.other ? ` · ${o.risk.other}` : ''}
              </td>
              <td className="px-3 py-2">
                {o.risk ? (
                  <select
                    value={o.risk.status}
                    onChange={(e) => obSetRisk(o.id, { status: e.target.value, reasons: o.risk!.reasons, other: o.risk!.other, ownerId: o.risk!.ownerId || o.ownerId, notes: o.risk!.notes }).then(() => { crmToast('Status updated', 'success'); onChanged() }).catch(() => crmToast('Owner required — open the panel from the lead', 'error'))}
                    className="rounded-lg border border-border bg-surface px-1.5 py-1 text-[11px] font-bold focus:outline-none"
                    style={{ color: RISK_COLOR[o.risk.status] }}
                  >
                    {RISK_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <button onClick={() => onOpen(o)} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">SUGGESTED — review</button>
                )}
              </td>
              <td className="px-3 py-2 min-w-[180px]">
                {o.risk ? (
                  <input
                    value={noteDraft[o.id] ?? o.risk.notes ?? ''}
                    onChange={(e) => setNoteDraft((x) => ({ ...x, [o.id]: e.target.value }))}
                    onBlur={() => {
                      const v = noteDraft[o.id]
                      if (v !== undefined && v !== (o.risk!.notes || '')) obSetRisk(o.id, { status: o.risk!.status, reasons: o.risk!.reasons, other: o.risk!.other, ownerId: o.risk!.ownerId || o.ownerId, notes: v }).then(() => crmToast('Note saved', 'success'))
                    }}
                    placeholder="Notes…"
                    className="w-full rounded-lg border border-border bg-surface px-2 py-1 text-[11px] focus:outline-none focus:border-brand-400"
                  />
                ) : <span className="text-txt-tertiary">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const CrmOnboardings: React.FC = () => {
  useObFont()
  const navigate = useNavigate()
  const [cfg, setCfg] = useState<ObConfig | null>(null)
  const [list, setList] = useState<Onboarding[]>([])
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState<any[]>([])
  const [view, setView] = useState<'board' | 'risk'>('board')
  const [q, setQ] = useState('')
  const [fOwner, setFOwner] = useState('')
  const [fPlan, setFPlan] = useState('')
  const [fStage, setFStage] = useState('')
  const [fRisk, setFRisk] = useState('')
  const [fFrom, setFFrom] = useState('')
  const [fTo, setFTo] = useState('')
  // Drag-with-reason (Win Probability pattern).
  const [dragId, setDragId] = useState<string | null>(null)
  // COLLAPSIBLE COLUMNS (Lee, 14 Oct 2026): collapsed columns shrink to a
  // strip; the others widen and cards go COMPACT (header + pills + focal
  // panel only) so more fit on screen. Remembered per browser.
  const [collapsedCols, setCollapsedCols] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('wq_ob_cols_collapsed') || '{}') } catch { return {} }
  })
  const toggleCol = (k: string) => setCollapsedCols((prev) => {
    const next = { ...prev, [k]: !prev[k] }
    try { localStorage.setItem('wq_ob_cols_collapsed', JSON.stringify(next)) } catch { /* ignore */ }
    return next
  })
  const [pendingMove, setPendingMove] = useState<{ id: string; column: string; name: string } | null>(null)
  const [reason, setReason] = useState('')
  const [completePrompt, setCompletePrompt] = useState<{ leadId: string; name: string } | null>(null)
  const [embedOpen, setEmbedOpen] = useState(false)
  const [feedSyncedAt, setFeedSyncedAt] = useState<string | null>(null)

  const reload = () => getOnboardings().then((r) => { setList(r.onboardings); setFeedSyncedAt(r.feedSyncedAt ?? null) }).catch(() => crmToast('Could not load onboardings', 'error')).finally(() => setLoading(false))
  useEffect(() => {
    getObConfig().then(setCfg).catch(() => {})
    getUsers().then(setUsers).catch(() => {})
    reload()
    const t = setInterval(reload, 60_000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const agentName = (id?: string | null) => users.find((u) => String(u.id) === String(id))?.name || (id ? id : 'Unassigned')
  const initials = (id?: string | null) => { const n = users.find((u) => String(u.id) === String(id))?.name; return n ? n.split(/\s+/).map((w: string) => w[0]).join('').toUpperCase().slice(0, 2) : null }

  // ── BOARD REDESIGN (Natalie's pack §2.1, Phase 3) ──
  const [tile, setTile] = useState<string>('')             // count tiles act as filters, one at a time
  const [boardView, setBoardView] = useState<'grouped' | 'list'>('grouped')
  const [draftWaiting, setDraftWaiting] = useState<Set<string>>(new Set())
  useEffect(() => {
    import('../../utils/api').then(({ apiGet }) =>
      apiGet<{ drafts: Array<{ onboardingId: string | null; leadId: string | null }> }>('/crm/ob-drafts?status=to_send')
        .then((r) => setDraftWaiting(new Set(r.drafts.flatMap((d) => [d.onboardingId, d.leadId].filter(Boolean) as string[]))))
        .catch(() => {}))
  }, [])
  /** §2.1 flags, priority order — top 2 show on the card. */
  const flagsOf = (o: Onboarding): string[] => {
    const f: string[] = []
    if (o.paymentStatus === 'pending') f.push('Awaiting payment')
    if (o.hoursAllowance && o.hoursUsed > o.hoursAllowance) f.push('Over plan')
    if (!o.nextMeeting && o.column === 'progress') f.push('No next call')
    return f
  }
  const subGroupOf = (o: Onboarding): string => {
    const total = (cfg?.stages || []).length || 9
    const i = o.currentStage ?? 0
    if (i <= 1) return 'Basic set-up'
    if (i >= total - 2) return 'Nearly there'
    return 'Training in progress'
  }
  const tileMatch = (o: Onboarding): boolean => {
    switch (tile) {
      case 'watching': return o.risk?.status === 'Watch'
      case 'inprogress': return o.column === 'progress'
      case 'drafts': return draftWaiting.has(o.id) || (!!o.leadId && draftWaiting.has(o.leadId))
      case 'nocall': return !o.nextMeeting && o.column === 'progress'
      case 'payment': return o.paymentStatus === 'pending'
      case 'overplan': return !!o.hoursAllowance && o.hoursUsed > o.hoursAllowance
      default: return true
    }
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return list.filter((o) => {
      if (needle && !`${o.companyName} ${o.stagingMeta?.contact?.name || ''} ${o.stagingMeta?.contact?.email || ''}`.toLowerCase().includes(needle)) return false
      if (fOwner && String(o.ownerId || '') !== fOwner) return false
      // 'awaiting' filter = anything without an active plan (incl. a pending Request an Onboarding).
      if (fPlan === 'awaiting' ? (o.planState !== 'awaiting' && o.planState !== 'requested') : fPlan && o.plan !== fPlan) return false
      if (fStage !== '' && o.currentStage !== Number(fStage)) return false
      if (fRisk && (o.risk?.status || '') !== fRisk) return false
      if (fFrom && (!o.goLiveDate || o.goLiveDate < fFrom)) return false
      if (fTo && (!o.goLiveDate || o.goLiveDate > fTo)) return false
      if (tile && !tileMatch(o)) return false
      return true
    })
  }, [list, q, fOwner, fPlan, fStage, fRisk, fFrom, fTo, tile, draftWaiting])

  const cols = cfg?.columns || []
  const byCol = useMemo(() => {
    const m: Record<string, Onboarding[]> = {}
    cols.forEach((c) => (m[c.key] = []))
    filtered.forEach((o) => { (m[o.column] = m[o.column] || []).push(o) })
    // NOT STARTED is curated (Lee, 13 Oct 2026, tightened): ONLY flagged
    // ONBOARDING REQUIRED companies and records with an assigned plan show
    // here. Ticks/hours/meetings do NOT surface a card — the old live book
    // carries stray auto-ticks from the staging trigger era (the 156 bug).
    if (m['notstarted']) {
      m['notstarted'] = m['notstarted'].filter((o) => o.planState === 'requested' || !!o.plan)
    }
    // SORT BY LAST MEETING (Lee, 13 Oct 2026): every column orders by the
    // most recent held meeting — the onboarding's own sessions first, the
    // lead's last CRM meeting as fallback; never-met companies sink to the
    // bottom. Not Started additionally keeps flagged ONBOARDING REQUIRED on
    // top (newest request first) before the same ordering.
    const lastMeet = (o: Onboarding) => {
      const a = o.lastHeldAt || ''
      const b = o.lastMeeting?.at || ''
      return a > b ? a : b
    }
    for (const key of Object.keys(m)) {
      m[key] = [...m[key]].sort((a, b) => {
        if (key === 'notstarted') {
          const ar = a.planState === 'requested' ? 1 : 0
          const br = b.planState === 'requested' ? 1 : 0
          if (ar !== br) return br - ar
          if (ar && br) {
            const rq = (b.requestedAt || '').localeCompare(a.requestedAt || '')
            if (rq) return rq
          }
        }
        return lastMeet(b).localeCompare(lastMeet(a))
      })
    }
    return m
  }, [filtered, cols])

  const onDrop = (col: string) => {
    if (!dragId) return
    const o = list.find((x) => x.id === dragId)
    setDragId(null)
    if (!o || o.column === col) return
    setReason('')
    setPendingMove({ id: o.id, column: col, name: o.companyName })
  }

  const confirmMove = async () => {
    if (!pendingMove || !reason.trim()) return
    try {
      await obSetOverride(pendingMove.id, pendingMove.column, reason.trim())
      const o = list.find((x) => x.id === pendingMove.id)
      crmToast('Moved manually — kept in the audit', 'success')
      // Non-blocking Complete prompt, only when a CRM lead is linked.
      if (pendingMove.column === 'complete' && o?.leadId) setCompletePrompt({ leadId: o.leadId, name: o.companyName })
      setPendingMove(null)
      reload()
    } catch (e: any) {
      crmToast(e?.message ? `Could not move: ${e.message}` : 'Could not move the card', 'error')
    }
  }

  // ── DESIGN REBUILD (Board.dc.html, 30 Oct 2026): mockup palette + anatomy.
  const pathwayOfShort = (short: string): string =>
    /crm/i.test(short) ? 'Data' : /bespoke/i.test(short) ? 'Proposal' : 'Training'
  const PATH_COL: Record<string, string> = { Training: '#1D4ED8', Data: '#8f4399', Proposal: '#0c8279' }
  const pathBars = (o: Onboarding) => {
    const g = new Map<string, { done: number; total: number; complete: boolean }>()
    ;(o.stages || []).forEach((s: any) => {
      if (s.hidden) return
      const p = pathwayOfShort(s.short || s.name || '')
      const cur = g.get(p) || { done: 0, total: 0, complete: true }
      cur.done += s.done || 0; cur.total += s.total || 0; cur.complete = cur.complete && !!s.complete
      g.set(p, cur)
    })
    return ['Training', 'Data', 'Proposal'].filter((p) => g.has(p)).map((p) => {
      const v = g.get(p)!
      const pct = v.total ? Math.round((v.done / v.total) * 100) : 0
      return { name: p, pct, done: v.complete, color: v.complete ? '#1F9D57' : PATH_COL[p] }
    })
  }
  const flagPill = (label: string, sev: number) => (
    <span key={label} style={{ fontSize: 11.5, fontWeight: 800, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap', ...(sev === 3 ? { background: '#DDF3E4', color: '#146C3A' } : sev === 2 ? { background: OB.redBg, color: OB.red } : sev === 1 ? { background: OB.amberBg, color: OB.amberDark } : { background: OB.track, color: OB.muted }) }}>{label}</span>
  )
  const cardFlags = (o: Onboarding): React.ReactNode[] => {
    const out: React.ReactNode[] = []
    if (o.planState === 'requested') out.push(flagPill('Onboarding required', 2))
    if (o.paymentStatus === 'pending') out.push(flagPill(`Awaiting payment${o.price ? ` £${Number(o.price).toLocaleString()}` : ''}`, 1))
    if (o.hoursAllowance && o.hoursUsed > o.hoursAllowance) out.push(flagPill('Over plan · chargeable', 2))
    if (!o.nextMeeting && o.column === 'progress') out.push(flagPill('No next call', 2))
    if (o.risk?.status) out.push(flagPill(o.risk.status, o.risk.status === 'Watch' ? 1 : 2))
    return out.slice(0, 2)
  }
  const COL_META: Record<string, { c: string; desc: string }> = {
    notstarted: { c: '#9AA5B4', desc: 'signed, awaiting payment or kick-off not held' },
    progress: { c: '#1D4ED8', desc: 'tagged Basic set-up (account and quoting set-up) or Training in progress' },
    complete: { c: '#146C3A', desc: 'graduated, in account mode' },
    archived: { c: '#5B6676', desc: 'did not go ahead' },
  }
  const tagOf = (o: Onboarding): { label: string; style: React.CSSProperties } | null => {
    if (o.column !== 'progress') return null
    const g = subGroupOf(o)
    if (g === 'Basic set-up') return { label: 'Basic set-up', style: { background: '#E4EDFC', color: '#1D4ED8' } }
    if (g === 'Training in progress') return { label: 'Training in progress', style: { background: OB.purpleBg, color: OB.purple } }
    return null
  }
  const feedMins = feedSyncedAt ? Math.round((Date.now() - new Date(feedSyncedAt).getTime()) / 60000) : null
  const feedLine = feedMins === null ? 'feed never synced' : feedMins < 60 ? `synced ${feedMins} min ago` : `synced ${Math.round(feedMins / 60)}h ago`
  const feedStale = feedMins === null || feedMins > 90
  const tileDefs: Array<[string, string, number, number]> = [
    ['watching', 'Watching', list.filter((o) => o.risk?.status === 'Watch').length, 3],
    ['inprogress', 'In onboarding', list.filter((o) => o.column === 'progress').length, 0],
    ['drafts', 'Follow-ups waiting', list.filter((o) => draftWaiting.has(o.id) || (!!o.leadId && draftWaiting.has(o.leadId!))).length, 1],
    ['nocall', 'No next call booked', list.filter((o) => !o.nextMeeting && o.column === 'progress').length, 1],
    ['payment', 'Awaiting payment', list.filter((o) => o.paymentStatus === 'pending').length, 1],
    ['overplan', 'Over plan hours', list.filter((o) => !!o.hoursAllowance && o.hoursUsed > o.hoursAllowance).length, 0],
  ]
  const sel8 = (on: boolean): React.CSSProperties => ({ font: 'inherit', fontSize: 13, fontWeight: 700, border: 0, borderRadius: 8, padding: '6px 14px', minHeight: 32, cursor: 'pointer', background: on ? OB.accent : 'transparent', color: on ? '#fff' : OB.ink2 })
  const inputCls = 'rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400'

  const BoardCard: React.FC<{ o: Onboarding }> = ({ o }) => {
    const meta = o.stagingMeta || {}
    const tag = boardView === 'grouped' ? tagOf(o) : null
    const flags = cardFlags(o)
    const bars = pathBars(o)
    const over = !!o.hoursAllowance && o.hoursUsed > o.hoursAllowance
    return (
      <div draggable onDragStart={() => setDragId(o.id)} onClick={() => navigate(`/crm/onboardings/co/${o.id}`)}
        className="ob-tile" style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, padding: '11px 12px 10px', display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0, cursor: 'pointer' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={o.companyName}>
            {countryToFlag(o.country) ? `${countryToFlag(o.country)} ` : ''}{o.companyName}
          </span>
          <span style={{ display: 'flex', gap: 2, flex: '0 0 auto', alignItems: 'center' }}>
            {initials(o.ownerId) && (
              <span title={agentName(o.ownerId)} style={{ width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 800, color: '#fff', background: personColor(agentName(o.ownerId)) }}>{initials(o.ownerId)}</span>
            )}
            <button onClick={(e) => { e.stopPropagation(); const arch = !o.archivedAt; if (!window.confirm(arch ? `Archive ${o.companyName}? It moves to the Archived column (nothing is deleted).` : `Unarchive ${o.companyName}?`)) return; obArchive(o.id, arch).then(() => { crmToast(arch ? 'Archived' : 'Unarchived', 'success'); reload() }).catch((err: any) => crmToast(err?.message || 'Failed', 'error')) }}
              title={o.archivedAt ? 'Unarchive' : 'Archive'} style={{ font: 'inherit', fontSize: 11, color: OB.faint, background: 'none', border: 0, cursor: 'pointer', padding: '0 2px' }}>{o.archivedAt ? '↩' : '×'}</button>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 800, background: OB.track, color: OB.ink2, borderRadius: 999, padding: '1px 8px' }}>{o.plan || 'No plan'}</span>
          <span style={{ fontWeight: over ? 800 : 700, color: over ? OB.red : OB.ink2 }}>
            {o.hoursAllowance ? `${Math.min(o.hoursUsed, o.hoursAllowance)}/${o.hoursAllowance}h${over ? ` + ${(o.hoursUsed - o.hoursAllowance).toFixed((o.hoursUsed - o.hoursAllowance) % 1 ? 1 : 0)}h over` : ''}` : `${o.hoursUsed}h`}
          </span>
          {meta.mrr ? <span style={{ marginLeft: 'auto', fontWeight: 800 }}>£{Number(meta.mrr).toLocaleString()}<span style={{ fontSize: 10, color: OB.muted, fontWeight: 700 }}>/mo</span></span> : null}
        </div>
        {tag && <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '1px 7px', alignSelf: 'flex-start', ...tag.style }}>{tag.label}</span>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: 12.5 }}>
          <span style={o.nextMeeting ? { fontWeight: 700, color: OB.ink } : { fontWeight: 800, color: OB.red }}>
            {o.nextMeeting ? `Next ${fmtDate(o.nextMeeting.startAt)} · ${new Date(o.nextMeeting.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'No next call'}
          </span>
          <span style={{ color: OB.muted }}>Last call {(o.lastHeldAt || o.lastMeeting?.at) ? `${fmtDate(o.lastHeldAt || o.lastMeeting?.at!)}${o.lastMeeting?.assigneeId ? ` · ${agentName(o.lastMeeting.assigneeId).split(' ')[0]}` : ''}` : '—'}</span>
          <span style={{ color: OB.muted }}>Last email {o.lastEmail?.at ? `${fmtDate(o.lastEmail.at)} · ${o.lastEmail.direction === 'out' ? 'us → them' : 'them → us'}` : '—'}</span>
          <span style={{ color: OB.muted }}>Go-live {o.goLiveDate ? new Date(o.goLiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'not set'}</span>
        </div>
        {flags.length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{flags}</div>}
        {bars.length > 0 && (
          <div style={{ display: 'flex', gap: 6, paddingTop: 2 }}>
            {bars.map((b) => (
              <span key={b.name} title={`${b.name}: ${b.done ? 'Done' : `${b.pct}% done`}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: OB.muted }}>{b.name}</span>
                <span style={{ display: 'block', height: 6, borderRadius: 3, background: OB.rowDiv, overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: 6, width: `${b.pct}%`, background: b.color }} />
                </span>
              </span>
            ))}
          </div>
        )}
        {o.hoursRequest && (
          <div style={{ borderRadius: 8, border: `1px solid ${OB.amberBorder}`, background: OB.amberPale, padding: '4px 8px' }} title={o.hoursRequest.note || undefined}>
            <span style={{ fontSize: 11, fontWeight: 800, color: OB.amberDark, display: 'inline-flex', alignItems: 'center', gap: 4 }}><ClockIcon size={11} /> Hours requested{o.hoursRequest.hours ? ` · +${o.hoursRequest.hours}h` : ''}{o.hoursRequest.note ? ` — "${o.hoursRequest.note}"` : ''}</span>
          </div>
        )}
        {(o.hoursOver || 0) > 0 && (
          <div style={{ borderRadius: 8, border: `1px solid ${OB.redBorder}`, background: OB.redPale, padding: '5px 9px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: OB.red, flex: 1 }}>{o.hoursOver}h over the plan — chargeable{o.overNotice ? ' · notified ✓' : ''}</span>
            <button onClick={(e) => { e.stopPropagation(); if (!window.confirm(`Send ${o.companyName} the over-hours notice?`)) return; obSendOverNotice(o.id).then(() => { crmToast('Over-hours notice sent', 'success'); reload() }).catch((err: any) => crmToast(err?.message || 'Could not send', 'error')) }}
              style={{ font: 'inherit', fontSize: 11, fontWeight: 800, background: OB.red, color: '#fff', border: 0, borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>{o.overNotice ? 'Notify again' : 'Notify'}</button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: OB.pageBg, fontFamily: OB.font, color: OB.ink }}>
      <CrmToaster />
      <div style={{ flex: 'none', padding: '16px 28px 0', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>

          {/* header */}
          <header style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Onboardings</h1>
              <span style={{ fontSize: 13, color: feedStale ? OB.red : OB.muted }}>
                {list.filter((o) => o.column === 'progress').length} in onboarding · {list.filter((o) => o.column === 'complete').length} complete · {list.filter((o) => o.column === 'archived' || o.archivedAt).length} archived · {feedStale ? <WarnIcon size={11} color={OB.red} /> : null} {feedLine}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <SegTabs size="sm" tabs={[{ key: 'board', label: 'Board' }, { key: 'risk', label: 'At Risk' }]} value={view} onChange={(k) => setView(k as any)} />
              <div role="group" aria-label="View" style={{ display: 'flex', background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 10, padding: 3 }}>
                <button onClick={() => setBoardView('list')} style={sel8(boardView === 'list')}>List</button>
                <button onClick={() => setBoardView('grouped')} style={sel8(boardView === 'grouped')}>Grouped</button>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 10, padding: '0 12px', minHeight: 40, width: 260, maxWidth: '100%', boxSizing: 'border-box' }}>
                <span style={{ fontSize: 13, color: OB.muted }}>Search</span>
                <input aria-label="Search companies" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company or contact" style={{ border: 0, outline: 0, font: 'inherit', flex: 1, minWidth: 0 }} />
              </label>
              <BtnOutline onClick={() => setEmbedOpen(true)} style={{ minHeight: 40, borderRadius: 10, padding: '8px 14px' }}>Embed</BtnOutline>
            </div>
          </header>

          {/* stat tiles */}
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            {tileDefs.map(([k, label, n, sev]) => {
              const on = tile === k
              const st: React.CSSProperties = on ? { background: OB.ink, border: `1.5px solid ${OB.ink}`, color: '#fff' }
                : sev === 3 ? { background: '#E4EDFC', border: '1.5px solid #1D4ED8', color: '#1D4ED8' }
                : sev === 1 ? { background: OB.amberPale, border: `1.5px solid ${OB.amberBorder}`, color: OB.amberInk }
                : { background: '#fff', border: `1.5px solid ${OB.border}`, color: OB.ink }
              return (
                <button key={k} onClick={() => setTile(on ? '' : k)} aria-pressed={on}
                  style={{ font: 'inherit', textAlign: 'left', cursor: 'pointer', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, height: 72, boxSizing: 'border-box', overflow: 'hidden', whiteSpace: 'nowrap', ...st }}>
                  <span style={{ fontSize: 22, fontWeight: 800 }}>{n}</span>
                  <span style={{ fontSize: 12.5 }}>{label}</span>
                </button>
              )
            })}
          </section>

          {/* filters */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={fOwner} onChange={(e) => setFOwner(e.target.value)} className={inputCls} title="Owner" style={{ minHeight: 36, borderRadius: 999, padding: '6px 14px' }}>
              <option value="">Owner: All</option>
              {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
            </select>
            <select value={fPlan} onChange={(e) => setFPlan(e.target.value)} className={inputCls} title="Plan" style={{ minHeight: 36, borderRadius: 999, padding: '6px 14px' }}>
              <option value="">Plan: All</option>
              <option value="awaiting">Awaiting plan</option>
              {Object.keys(cfg?.plans || {}).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={fStage} onChange={(e) => setFStage(e.target.value)} className={inputCls} title="Current stage" style={{ minHeight: 36, borderRadius: 999, padding: '6px 14px' }}>
              <option value="">Stage: All</option>
              {(cfg?.stages || []).map((s, i) => <option key={s.short} value={String(i)}>{s.short}</option>)}
            </select>
            <select value={fRisk} onChange={(e) => setFRisk(e.target.value)} className={inputCls} title="At Risk status" style={{ minHeight: 36, borderRadius: 999, padding: '6px 14px' }}>
              <option value="">Flags: Any</option>
              {(cfg?.riskStatuses || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <span style={{ fontSize: 12.5, color: OB.muted }}>Go-live</span>
            <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className={inputCls} />
            <span style={{ fontSize: 12.5, color: OB.muted }}>–</span>
            <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className={inputCls} />
            {(fOwner || fPlan || fStage || fRisk || fFrom || fTo) && (
              <button onClick={() => { setFOwner(''); setFPlan(''); setFStage(''); setFRisk(''); setFFrom(''); setFTo('') }} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, color: OB.accent, background: 'none', border: 0, cursor: 'pointer' }}>Clear</button>
            )}
            <span style={{ fontSize: 12.5, color: OB.muted, marginLeft: 'auto' }}>
              {tile ? `Showing: ${tileDefs.find((t) => t[0] === tile)?.[1]} · click the tile again to clear` : boardView === 'list' ? 'One row per customer' : 'Click « on a column to fold it, or a folded strip to open it'}
            </span>
          </div>

      </div>

      {/* body — fixed chrome above, the lists scroll (Lee, 30 Oct) */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '14px 28px 16px' }}>
          {view === 'risk' ? (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <RiskTable rows={filtered.filter((o) => o.risk || (o.suggestions?.length ?? 0) > 0)} users={users} agentName={agentName} onOpen={(o) => navigate(o.leadId ? `/crm/lead/${o.leadId}?tab=onboarding` : `/crm/onboardings/${o.id}`)} onChanged={reload} />
            </div>
          ) : loading ? (
            <SkeletonRows rows={5} rowClass="h-20 rounded-xl" />
          ) : list.length === 0 ? (
            <Card style={{ border: `1px dashed ${OB.inputBorder}`, maxWidth: 520, margin: '32px auto', textAlign: 'center', padding: 32 }}>
              <strong style={{ fontSize: 14 }}>No onboardings yet</strong>
              <span style={{ fontSize: 13, color: OB.ink2 }}>The WeQuote feed syncs automatically every 30 minutes — companies appear here as it pulls them.</span>
            </Card>
          ) : boardView === 'list' ? (
            <section style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 14, overflow: 'auto', flex: 1, minHeight: 0 }}>
              <div style={{ minWidth: 1040 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2.1fr 1.1fr 1.3fr 1.2fr 1.2fr 1.3fr 1.5fr 1.4fr', gap: 12, padding: '10px 16px', fontSize: 11, fontWeight: 800, color: OB.muted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${OB.border}` }}>
                  <span>Company</span><span>Stage</span><span>Plan · hours</span><span>Next call</span><span>Last call</span><span>Last email</span><span>Flags</span><span>Pathways</span>
                </div>
                {filtered.filter((o) => o.column !== 'archived' && !o.archivedAt).map((o) => {
                  const over = !!o.hoursAllowance && o.hoursUsed > o.hoursAllowance
                  const bars = pathBars(o)
                  return (
                    <div key={o.id} className="ob-row" onClick={() => navigate(`/crm/onboardings/co/${o.id}`)}
                      style={{ display: 'grid', gridTemplateColumns: '2.1fr 1.1fr 1.3fr 1.2fr 1.2fr 1.3fr 1.5fr 1.4fr', gap: 12, padding: '9px 16px', alignItems: 'center', borderBottom: `1px solid ${OB.rowDiv}`, fontSize: 12.5, cursor: 'pointer' }}>
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
                        {initials(o.ownerId) && <span title={agentName(o.ownerId)} style={{ width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 800, color: '#fff', background: personColor(agentName(o.ownerId)), flex: '0 0 24px' }}>{initials(o.ownerId)}</span>}
                        <span style={{ fontWeight: 800, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.companyName}</span>
                      </span>
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: tagOf(o)?.label === 'Basic set-up' ? '#1D4ED8' : tagOf(o) ? OB.purple : o.column === 'complete' ? '#146C3A' : OB.muted }}>{tagOf(o)?.label || cfg?.columns.find((c) => c.key === o.column)?.label || o.column}</span>
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, background: OB.track, color: OB.ink2, borderRadius: 999, padding: '1px 8px' }}>{o.plan || '—'}</span>
                        <span style={over ? { fontWeight: 800, color: OB.red } : { fontWeight: 700, color: OB.ink2 }}>{o.hoursAllowance ? `${o.hoursUsed}/${o.hoursAllowance}h` : `${o.hoursUsed}h`}</span>
                      </span>
                      <span style={o.nextMeeting ? { color: OB.ink, fontWeight: 700 } : { color: OB.red, fontWeight: 800 }}>{o.nextMeeting ? fmtDate(o.nextMeeting.startAt) : 'None booked'}</span>
                      <span style={{ color: OB.ink2 }}>{(o.lastHeldAt || o.lastMeeting?.at) ? fmtDate(o.lastHeldAt || o.lastMeeting?.at!) : '—'}</span>
                      <span style={{ color: OB.ink2 }}>{o.lastEmail?.at ? `${fmtDate(o.lastEmail.at)} · ${o.lastEmail.direction === 'out' ? 'us → them' : 'them → us'}` : '—'}</span>
                      <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{cardFlags(o)}</span>
                      <span style={{ display: 'flex', gap: 5 }}>
                        {bars.map((b) => (
                          <span key={b.name} title={`${b.name}: ${b.done ? 'Done' : `${b.pct}% done`}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: OB.muted }}>{b.name}</span>
                            <span style={{ display: 'block', height: 6, borderRadius: 3, background: OB.rowDiv, overflow: 'hidden' }}><span style={{ display: 'block', height: 6, width: `${b.pct}%`, background: b.color }} /></span>
                          </span>
                        ))}
                      </span>
                    </div>
                  )
                })}
              </div>
            </section>
          ) : (
            <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: cols.map((c) => (collapsedCols[c.key] ? '44px' : 'minmax(250px, 1fr)')).join(' '), gap: 12, alignItems: 'stretch', minWidth: 900, height: '100%', boxSizing: 'border-box' }}>
                {cols.map((c) => {
                  const m = COL_META[c.key] || { c: c.color, desc: '' }
                  const listC = byCol[c.key] || []
                  if (collapsedCols[c.key]) return (
                    <button key={c.key} onClick={() => toggleCol(c.key)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.key)}
                      aria-label={`Show ${c.label}`}
                      style={{ font: 'inherit', cursor: 'pointer', background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, height: '100%', boxSizing: 'border-box', width: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flex: '0 0 9px', background: m.c }} />
                      <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 13, fontWeight: 800, color: OB.ink2 }}>{c.label} · {listC.length}</span>
                      <span style={{ fontSize: 13, color: OB.accent, fontWeight: 800 }}>»</span>
                    </button>
                  )
                  const ordered = c.key === 'progress'
                    ? [...listC].sort((a, b) => ['Basic set-up', 'Training in progress', 'Nearly there'].indexOf(subGroupOf(a)) - ['Basic set-up', 'Training in progress', 'Nearly there'].indexOf(subGroupOf(b)))
                    : listC
                  return (
                    <section key={c.key} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.key)}
                      style={{ background: '#EBEFF4', borderRadius: 14, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, height: '100%', boxSizing: 'border-box', minHeight: 0 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 4px' }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flex: '0 0 9px', background: m.c }} />
                        <span style={{ fontSize: 14, fontWeight: 800 }}>{c.label}</span>
                        <span style={{ fontSize: 13, color: OB.muted }}>{listC.length}</span>
                        <button onClick={() => toggleCol(c.key)} style={{ font: 'inherit', marginLeft: 'auto', background: 'none', border: 0, color: OB.accent, fontWeight: 800, cursor: 'pointer' }}>«</button>
                      </div>
                      {m.desc && <div style={{ fontSize: 11.5, color: OB.muted, padding: '0 4px', lineHeight: 1.35 }}>{m.desc}</div>}
                      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 2 }}>
                        {ordered.map((o, oi, arr) => {
                          const showGroupHeader = c.key === 'progress' && (oi === 0 || subGroupOf(arr[oi - 1]) !== subGroupOf(o))
                          return (
                            <React.Fragment key={o.id}>
                              {showGroupHeader && <p style={{ margin: 0, paddingTop: oi ? 4 : 0, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: OB.muted }}>{subGroupOf(o)} · {arr.filter((x) => subGroupOf(x) === subGroupOf(o)).length}</p>}
                              <BoardCard o={o} />
                            </React.Fragment>
                          )
                        })}
                        {listC.length === 0 && <div style={{ borderRadius: 12, border: `1px dashed ${OB.inputBorder}`, padding: 16, textAlign: 'center', fontSize: 12, color: OB.muted }}>Drop a card here</div>}
                      </div>
                    </section>
                  )
                })}
              </div>
            </div>
          )}
      </div>
      {/* Manual-move reason popover (Win Probability pattern). */}
      {pendingMove && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,.5)' }} onClick={() => setPendingMove(null)}>
          <div className="w-[420px] rounded-xl bg-surface border border-border shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-[14px] font-bold text-txt-primary">Move “{pendingMove.name}”?</p>
            <p className="text-[12px] text-txt-secondary mt-0.5">Manual moves override the automatic column until the next automatic transition.</p>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (kept in the audit)…"
              rows={2}
              className="mt-3 w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400 resize-none"
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="secondary" onClick={() => setPendingMove(null)}>Cancel</Button>
              <Button size="sm" onClick={confirmMove} disabled={!reason.trim()}>Confirm</Button>
            </div>
          </div>
        </div>
      )}

      {embedOpen && <ObEmbedModal list={list} onClose={() => setEmbedOpen(false)} />}

      {/* Non-blocking Complete prompt. */}
      {completePrompt && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] rounded-xl bg-surface border border-border shadow-xl px-4 py-3 flex items-center gap-3">
          <p className="text-[12px] text-txt-primary">Also move <b>{completePrompt.name}</b> to <b>Onboarding Complete</b> in the pipeline?</p>
          <Button size="sm" onClick={() => {
            moveDealStage(completePrompt.leadId, 'onboarding_complete')
              .then(() => crmToast('Lead moved to Onboarding Complete', 'success'))
              .catch(() => crmToast('Could not move the lead', 'error'))
            setCompletePrompt(null)
          }}>Yes, move it</Button>
          <Button size="sm" variant="ghost" onClick={() => setCompletePrompt(null)}>No</Button>
        </div>
      )}
    </div>
  )
}
